import {
  BaseError,
  ContractFunctionRevertedError,
} from "viem";

/**
 * Guard for batched `Promise.allSettled` calls under viem's
 * `batch.multicall`. If every call rejected — i.e. the underlying
 * `Multicall3` eth_call itself failed (transport error, batch revert,
 * Multicall3 missing on the chain) — re-throws the first rejection so the
 * caller can surface it as an error instead of silently zero-filling.
 * Otherwise returns the input untouched for per-call handling.
 */
export function requireAnySettled<
  T extends readonly PromiseSettledResult<unknown>[]
>(results: T): T {
  if (results.every((r) => r.status === "rejected")) {
    throw (results[0] as PromiseRejectedResult).reason;
  }
  return results;
}

/**
 * Single-result handler for the batched-allSettled pattern above. Only an
 * actual on-chain revert (a `ContractFunctionRevertedError` somewhere in
 * the error's cause chain) is treated as zero; any other rejection
 * (transport failure, decoding error, etc.) propagates so it can be
 * surfaced rather than masked as a healthy zero result.
 */
export function settledOrZero(
  result: PromiseSettledResult<bigint>
): bigint {
  if (result.status === "fulfilled") return result.value;
  if (isRevertError(result.reason)) return 0n;
  throw result.reason;
}

/**
 * True iff `error` carries a `ContractFunctionRevertedError` in its
 * `cause` chain — i.e. an on-chain revert as opposed to a transport,
 * decoding, or multicall-batch failure.
 */
export function isRevertError(error: unknown): boolean {
  if (!(error instanceof BaseError)) return false;
  return !!error.walk((e) => e instanceof ContractFunctionRevertedError);
}
